import { useMemo, useState } from "react";
import { useMutation } from "convex/react";
import { api } from "@constructionautomation/backend";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { toast } from "sonner";

import type { RowGroup, SelectedRows } from "./types";

type GroupMemberInput = { tableId: string; rowIndex: number };
type CreateGroupArgs = {
  name: string;
  members: GroupMemberInput[];
  directions?: string;
};
type UpdateGroupArgs = {
  name?: string;
  members?: GroupMemberInput[];
  directions?: string;
};

export function useRowGroupActions(
  submittalId: Id<"specPilotSubmittals">,
  rowGroups: RowGroup[],
  onAction?: () => void | Promise<void>
) {
  const updateSelection = useMutation(api.specPilot.extractedTables.updateSelection);
  const createRowGroup = useMutation(api.specPilot.rowGroups.create);
  const updateRowGroup = useMutation(api.specPilot.rowGroups.update);
  const removeRowGroup = useMutation(api.specPilot.rowGroups.remove);

  const [selectedRows, setSelectedRows] = useState<SelectedRows>(new Map());

  const groupedRowsLookup = useMemo(() => {
    const lookup = new Map<string, { groupId: string; groupName: string }>();
    for (const group of rowGroups) {
      for (const member of group.members) {
        lookup.set(`${member.tableId}:${member.rowIndex}`, {
          groupId: group._id,
          groupName: group.name,
        });
      }
    }
    return lookup;
  }, [rowGroups]);

  async function handleSelectionChange(
    tableId: Id<"specPilotExtractedTables">,
    selected: boolean
  ) {
    try {
      await updateSelection({ tableId, selected });
      void onAction?.();
      if (!selected) {
        setSelectedRows((prev) => {
          const next = new Map(prev);
          next.delete(tableId);
          return next;
        });
      }
      return true;
    } catch (error) {
      toast.error("Failed to update selection");
      console.error(error);
      return false;
    }
  }

  function handleRowToggle(tableId: string, rowIndex: number, checked: boolean) {
    const groupedMeta = groupedRowsLookup.get(`${tableId}:${rowIndex}`);
    if (groupedMeta) return;

    setSelectedRows((prev) => {
      const next = new Map(prev);
      const current = new Set(next.get(tableId) ?? []);

      if (checked) {
        current.add(rowIndex);
      } else {
        current.delete(rowIndex);
      }

      if (current.size === 0) {
        next.delete(tableId);
      } else {
        next.set(tableId, current);
      }
      return next;
    });
  }

  function clearSelectedRows() {
    setSelectedRows(new Map());
  }

  function replaceSelectedRowsForTable(tableId: string, rowIndices: number[]) {
    setSelectedRows(() => {
      const next = new Map<string, Set<number>>();
      if (rowIndices.length > 0) {
        next.set(tableId, new Set(rowIndices));
      }
      return next;
    });
  }

  async function handleCreateGroup(args: CreateGroupArgs) {
    try {
      const payload: {
        submittalId: Id<"specPilotSubmittals">;
        name: string;
        members: { tableId: Id<"specPilotExtractedTables">; rowIndex: number }[];
        directions?: string;
      } = {
        submittalId,
        name: args.name,
        members: args.members.map((member) => ({
          tableId: member.tableId as Id<"specPilotExtractedTables">,
          rowIndex: member.rowIndex,
        })),
      };
      if (args.directions !== undefined) {
        payload.directions = args.directions;
      }

      await createRowGroup(payload);
      void onAction?.();
      toast.success("Rows merged");
    } catch (error) {
      toast.error("Failed to merge rows");
      console.error(error);
      throw error;
    }
  }

  async function handleUpdateGroup(groupId: string, args: UpdateGroupArgs) {
    try {
      const payload: {
        groupId: Id<"specPilotRowGroups">;
        name?: string;
        members?: { tableId: Id<"specPilotExtractedTables">; rowIndex: number }[];
        directions?: string;
      } = {
        groupId: groupId as Id<"specPilotRowGroups">,
      };
      if (args.name !== undefined) payload.name = args.name;
      if (args.members !== undefined) {
        payload.members = args.members.map((member) => ({
          tableId: member.tableId as Id<"specPilotExtractedTables">,
          rowIndex: member.rowIndex,
        }));
      }
      if (args.directions !== undefined) payload.directions = args.directions;

      await updateRowGroup(payload);
      void onAction?.();
      toast.success("Merged rows updated");
    } catch (error) {
      toast.error("Failed to update merged rows");
      console.error(error);
      throw error;
    }
  }

  async function handleDeleteGroup(groupId: string) {
    try {
      await removeRowGroup({ groupId: groupId as Id<"specPilotRowGroups"> });
      void onAction?.();
      toast.success("Merged rows deleted");
    } catch (error) {
      toast.error("Failed to delete merged rows");
      console.error(error);
    }
  }

  return {
    selectedRows,
    groupedRowsLookup,
    handleSelectionChange,
    handleRowToggle,
    replaceSelectedRowsForTable,
    clearSelectedRows,
    handleCreateGroup,
    handleUpdateGroup,
    handleDeleteGroup,
  };
}
